import { useEffect, useRef, useState, useCallback } from "react";

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

interface UseIntervalAutoSaveOptions {
  // Check if there are unsaved changes
  hasUnsavedChanges: () => boolean;

  // Function to trigger save
  onSave: () => Promise<void>;

  // Interval in milliseconds (default: 60000 = 1 minute)
  interval?: number;

  // Feature flag
  enabled?: boolean;

  // Callbacks
  onSaveStart?: () => void;
  onSaveEnd?: () => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for interval-based auto-save
 * Checks for unsaved changes every X milliseconds and saves if needed
 */
export function useIntervalAutoSave(options: UseIntervalAutoSaveOptions) {
  const {
    hasUnsavedChanges,
    onSave,
    interval = 60000, // 1 minute default
    enabled = true,
    onSaveStart,
    onSaveEnd,
    onSuccess,
    onError,
  } = options;

  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>("idle");
  const isSaving = useRef(false);
  const saveCount = useRef(0);
  const lastSaveTime = useRef<number>(0);

  // Store the latest callback in a ref to avoid resetting the interval
  const performAutoSaveRef = useRef<(() => Promise<void>) | null>(null);

  // The actual save function
  const performAutoSave = useCallback(async () => {
    // Validation 1: Feature enabled?
    if (!enabled) {
      console.log("⏭️ Auto-save disabled");
      return;
    }

    // Validation 2: Already saving?
    if (isSaving.current) {
      console.log("⏸️ Save already in progress - skipping interval");
      return;
    }

    // Validation 3: Any unsaved changes?
    if (!hasUnsavedChanges()) {
      console.log("✨ No unsaved changes - skipping auto-save");
      return;
    }

    // All validations passed - proceed with save
    try {
      isSaving.current = true;
      setSaveStatus("saving");
      saveCount.current += 1;
      lastSaveTime.current = Date.now();

      console.log("💾 AUTO-SAVE TRIGGERED (Interval)", {
        count: saveCount.current,
        timestamp: new Date().toISOString(),
      });

      // Notify start
      onSaveStart?.();

      // Execute save
      await onSave();

      setSaveStatus("saved");
      console.log("✅ AUTO-SAVE SUCCESS");

      // Notify success
      onSuccess?.();
      onSaveEnd?.();

      // Show "saved" status briefly
      setTimeout(() => {
        if (!isSaving.current) {
          setSaveStatus("idle");
        }
      }, 3000);
    } catch (error) {
      console.error("❌ AUTO-SAVE FAILED:", error);
      setSaveStatus("error");

      // Notify error
      onError?.(error as Error);
      onSaveEnd?.();

      // Show error longer
      setTimeout(() => {
        if (!isSaving.current) {
          setSaveStatus("idle");
        }
      }, 10000);
    } finally {
      isSaving.current = false;
    }
  }, [
    enabled,
    hasUnsavedChanges,
    onSave,
    onSaveStart,
    onSaveEnd,
    onSuccess,
    onError,
  ]);

  // Update the ref whenever performAutoSave changes
  useEffect(() => {
    performAutoSaveRef.current = performAutoSave;
  }, [performAutoSave]);

  // Set up interval (doesn't reset when performAutoSave changes)
  useEffect(() => {
    if (!enabled) {
      console.log("⏭️ Auto-save interval not started (disabled)");
      return;
    }

    console.log(`⏰ Starting auto-save interval (${interval / 1000} seconds)`);

    const intervalId = setInterval(() => {
      console.log("⏰ Auto-save interval tick - checking for unsaved changes");
      // Call the latest version from the ref
      performAutoSaveRef.current?.();
    }, interval);

    return () => {
      console.log("🧹 Clearing auto-save interval");
      clearInterval(intervalId);
    };
  }, [enabled, interval]); // ✅ Removed performAutoSave from dependencies

  // Force save function (for manual trigger or cleanup)
  const forceSave = useCallback(async () => {
    if (hasUnsavedChanges()) {
      console.log("🚨 FORCE SAVE triggered");
      await performAutoSave();
    } else {
      console.log("⏭️ Force save skipped - no unsaved changes");
    }
  }, [hasUnsavedChanges, performAutoSave]);

  return {
    saveStatus,
    forceSave,
    isSaving: isSaving.current,
    saveCount: saveCount.current,
    lastSaveTime: lastSaveTime.current,
  };
}
